import { createAsyncThunk } from "@reduxjs/toolkit";

import { type ChatMessage } from "@fitness-app/data-models/entities/Chat";

import { type AsyncThunkCreator } from "../../../index";
import { CHAT_REDUCER_NAME } from "../types";
import { markChatMessagesAsRead } from "./markChatMessagesAsRead";
import { subscribeToChannelMessage } from "./subscribeToChannelMessage";
import { subscribeToChannelMessageMobile } from "./subscribeToChannelMessageMobile";

type Payload = {
  channelId: string;
  mobile?: boolean;
  withoutLoader?: boolean;
  /**
   * Refresh without opening a new realtime socket: for the refetch the socket itself triggers
   * on reconnect (resubscribing there would recurse forever) and for a tab return, where a live
   * socket must stay live - swapping it opens a window in which nothing is listening.
   */
  keepSubscription?: boolean;
};

export const getInitialChannelMessages = createAsyncThunk<ChatMessage[], Payload, AsyncThunkCreator<string>>(
  `${CHAT_REDUCER_NAME}/getInitialChannelMessages`,
  async (payload, { rejectWithValue, getState, dispatch, extra: { db, parties } }) => {
    const { messagesBatchSize, unreadMessages } = getState().chat;
    const unreadMessagesNumber = unreadMessages?.perChannel[payload.channelId] || 0;

    const batchSize =
      typeof unreadMessages === "number" || !unreadMessagesNumber
        ? messagesBatchSize
        : unreadMessagesNumber.length > messagesBatchSize
          ? unreadMessagesNumber.length
          : messagesBatchSize;

    const { error, data } = await db
      .from("chat_message")
      .select(
        "*, metadata:chat_message_metadata(transcription, summary, context, sentiment, issueType, actionable, dissatisfactionDetails, agentReplyStatus, agentReplyText, agentReplyOwnerTeamMemberId, agentReplyGeneratedAt, agentReplyResolvedAt, agentReplyResolvedByUserId, agentReplySentMessageId, agentReplyFeedbackVote, agentReplyFeedbackComment, agentReplyFeedbackSubmittedAt, agentReplyFeedbackUserId)",
      )
      .order("createdAt", { ascending: false })
      .eq("deleted", false)
      .eq("channelId", payload.channelId)
      .limit(batchSize)
      .returns<ChatMessage[]>();

    if (error) {
      return rejectWithValue(error.message);
    }

    if (payload.mobile) {
      dispatch(subscribeToChannelMessageMobile(payload.channelId));
    } else if (payload.keepSubscription) {
      // A socket that went stale while the machine slept may not know it yet - the library only
      // reacts to close/error - so a refresh that keeps it also nudges one that is not open.
      const socket = parties.chatRooms[payload.channelId];
      if (socket && socket.readyState !== WebSocket.OPEN) {
        socket.reconnect();
      }
    } else {
      dispatch(
        subscribeToChannelMessage(payload.channelId, {
          onReconnect: () =>
            void dispatch(
              getInitialChannelMessages({ channelId: payload.channelId, withoutLoader: true, keepSubscription: true }),
            ),
        }),
      );
    }

    // Clients used to markAllAsRead() here — harmless with a single channel, but with the
    // announcement channel it wiped the other channel's unread badge unseen (TRE-1947).
    // The per-channel RPC works per auth.uid(), so every role takes the same path.
    // A background refresh of a minimized window is not the reader looking at it, though -
    // the same guard the live handler applies before marking a new message.
    const activeChannel = getState().chat.activeChannels.find((channel) => channel.id === payload.channelId);
    if (!(payload.withoutLoader && activeChannel?.minimized)) {
      void dispatch(
        markChatMessagesAsRead({
          channelId: payload.channelId,
          messages: data.map((item) => ({
            id: item.id,
            createdAt: item.createdAt,
          })),
        }),
      );
    }

    return data;
  },
  {
    condition: ({ channelId, withoutLoader }, { getState }) => {
      const channelStatus = getState().chat.channelsStatus[channelId];

      if (channelStatus?.fetchingFirstPage) {
        // Already in progress, don't need to re-fetch
        return false;
      }
      // A refresh merges into the list, so it may overlap a page load; a first load must not.
      if (!withoutLoader && channelStatus?.fetchingNextPage) {
        return false;
      }
    },
  },
);
